const POINTS_PER_CSS_PIXEL = 72 / 96;
const UP_FILTER = 2;
const STRIP_ROWS = 256;
const encoder = new TextEncoder();

/** The page size in CSS pixels: the capture at 1x, any export padding included. */
export interface PageSize {
  width: number;
  height: number;
}

/**
 * A one-page PDF that shows the canvas, lossless, on a page of the given size.
 * Transparent pixels are drawn on white.
 */
export async function imagePdf(canvas: HTMLCanvasElement, page: PageSize, title: string): Promise<Blob> {
  const { width, height } = canvas;
  const context = canvas.getContext('2d');
  if (!context || width === 0 || height === 0) {
    throw new Error('Nothing to draw into the PDF.');
  }
  const pixels = await compressedRows(context, width, height);
  const pageWidth = points(page.width);
  const pageHeight = points(page.height);
  const drawing = encoder.encode(`q ${pageWidth} 0 0 ${pageHeight} 0 0 cm /Im0 Do Q`);
  return writePdf([
    ['<< /Type /Catalog /Pages 2 0 R >>'],
    ['<< /Type /Pages /Kids [3 0 R] /Count 1 >>'],
    [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`],
    [
      `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8`
      + ` /Filter /FlateDecode /DecodeParms << /Predictor 15 /Colors 3 /BitsPerComponent 8 /Columns ${width} >>`
      + ` /Length ${pixels.length} >>`,
      pixels,
    ],
    [`<< /Length ${drawing.length} >>`, drawing],
    [`<< /Title ${pdfText(title)} /Producer (Localintel) >>`],
  ]);
}

async function compressedRows(context: CanvasRenderingContext2D, width: number, height: number): Promise<Uint8Array> {
  const compression = new CompressionStream('deflate');
  const compressed = new Response(compression.readable).arrayBuffer();
  const writer = compression.writable.getWriter();
  const rowLength = width * 3;
  let above = new Uint8Array(rowLength);
  for (let top = 0; top < height; top += STRIP_ROWS) {
    const rows = Math.min(STRIP_ROWS, height - top);
    const rgba = context.getImageData(0, top, width, rows).data;
    const strip = new Uint8Array((rowLength + 1) * rows);
    for (let row = 0; row < rows; row++) {
      const current = new Uint8Array(rowLength);
      const out = row * (rowLength + 1);
      strip[out] = UP_FILTER;
      for (let x = 0; x < width; x++) {
        const source = (row * width + x) * 4;
        const alpha = rgba[source + 3] / 255;
        for (let channel = 0; channel < 3; channel++) {
          const value = Math.round(rgba[source + channel] * alpha + 255 * (1 - alpha));
          current[x * 3 + channel] = value;
          strip[out + 1 + x * 3 + channel] = (value - above[x * 3 + channel]) & 0xff;
        }
      }
      above = current;
    }
    await writer.write(strip);
  }
  await writer.close();
  return new Uint8Array(await compressed);
}

function points(cssPixels: number): string {
  return (Math.round(cssPixels * POINTS_PER_CSS_PIXEL * 100) / 100).toString();
}

function pdfText(text: string): string {
  const hex = Array.from(text, (character: string) => {
    const units: string[] = [];
    for (let index = 0; index < character.length; index++) {
      units.push(character.charCodeAt(index).toString(16).padStart(4, '0'));
    }
    return units.join('');
  }).join('');
  return `<FEFF${hex.toUpperCase()}>`;
}

type PdfObject = [dictionary: string, stream?: Uint8Array];

function writePdf(objects: PdfObject[]): Blob {
  const parts: Uint8Array[] = [];
  let length = 0;
  const push = (part: Uint8Array | string): void => {
    const bytes = typeof part === 'string' ? encoder.encode(part) : part;
    parts.push(bytes);
    length += bytes.length;
  };
  push('%PDF-1.4\n%âãÏÓ\n');
  const offsets = objects.map(([dictionary, stream]: PdfObject, index: number) => {
    const offset = length;
    push(`${index + 1} 0 obj\n${dictionary}\n`);
    if (stream) {
      push('stream\n');
      push(stream);
      push('\nendstream\n');
    }
    push('endobj\n');
    return offset;
  });
  const xref = length;
  push(`xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`);
  offsets.forEach((offset: number) => push(`${offset.toString().padStart(10, '0')} 00000 n \n`));
  push(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R /Info ${objects.length} 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return new Blob(parts as BlobPart[], { type: 'application/pdf' });
}
